// Import iOS integration for native features
import { iOS } from './native'

// Import Vercel Analytics
import { initializeAnalytics } from './analytics'

// Import global Logger so Shift+L works across the entire app
import { Logger } from './utils/Logger'

// Global Shift+L handler — toggles debug logging from any scene
document.addEventListener('keydown', (event: KeyboardEvent) => {
  if (event.code === 'KeyL' && event.shiftKey) {
    const enabled = Logger.getInstance().toggle()
    console.log('Debug logging:', enabled ? 'enabled' : 'disabled')
  }
})

// Dynamic import for Phaser to defer loading until needed
const initializeGame = async () => {
  // Get initial loading screen
  const initialLoading = document.querySelector('.initial-loading')

  try {
    // Initialize Vercel Analytics and Speed Insights
    initializeAnalytics()

    // Initialize iOS integration if on native platform
    if (iOS.isRunningOnNative()) {
      Logger.getInstance().log('Initializing iOS integration...')
      // Hide the iOS system status bar for full-screen gameplay
      await iOS.hideStatusBar()
    }

    // Initialize orientation manager for mobile devices
    const { OrientationManager } = await import('./utils/OrientationManager')
    const orientationManager = new OrientationManager()

    // Update loading message
    const subtitle = document.querySelector('.loading-subtitle')
    if (subtitle) subtitle.textContent = 'Loading game scenes...'    // Dynamically import Phaser
    const { default: Phaser } = await import('phaser')

    // Use dynamic imports for scenes to enable code splitting
    if (subtitle) subtitle.textContent = 'Initializing game...'

    const [
      { default: Boot },
      { default: Title },
      { default: ModeSelection },
      { default: Game },
      { default: Learn },
      { default: Settings },
      { default: Help },
      { default: ControllerSchemeSelector },
      { default: ScreenshotManager }
    ] = await Promise.all([
      import('./scenes/Boot'),
      import('./scenes/Title'),
      import('./scenes/ModeSelection'),
      import('./scenes/Game'),
      import('./scenes/Learn'),
      import('./scenes/Settings'),
      import('./scenes/Help'),
      import('./scenes/ControllerSchemeSelector'),
      import('./scenes/ScreenshotManager')
    ])

    // Remove initial loading screen
    if (initialLoading && initialLoading instanceof HTMLElement) {
      initialLoading.style.opacity = '0'
      initialLoading.style.transition = 'opacity 0.5s ease-out'
      setTimeout(() => initialLoading.remove(), 500)
    }

    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: 'app',
      width: 960,   // Increased from 480 for better desktop layout
      height: 640,  // Adjusted from 800 to maintain good proportions
      backgroundColor: '#f9e7ef',
      scale: {
        mode: Phaser.Scale.RESIZE, // Use RESIZE to fill screen without letterboxing
        autoCenter: Phaser.Scale.CENTER_BOTH
      },
      input: {
        gamepad: true,
        touch: {
          capture: true,  // Capture touch events
          target: null    // Use default canvas
        },
        mouse: {
          preventDefaultWheel: true
        },
        activePointers: 3  // Support multi-touch
      },
      scene: [Boot, Title, ModeSelection, Game, Learn, Settings, Help, ControllerSchemeSelector, ScreenshotManager]
    }

    const game = new Phaser.Game(config)

    // Maestro DOM hooks (no-op unless built with MAESTRO_HOOKS=1).
    // The import is unconditional so Vite's tree-shaker can drop the module
    // when `__MAESTRO_HOOKS__` is `false`; `installMaestroTestHooks` itself
    // also short-circuits in that case.
    try {
      const { installMaestroTestHooks } = await import('./utils/MaestroTestHooks')
      installMaestroTestHooks(game)
    } catch (e) {
      // Hook installation must never break the game.
      console.warn('Maestro hook install skipped:', e)
    }

    // Expose game instance and utilities for testing
    if (typeof window !== 'undefined') {
      (window as any).game = game

      // Expose GameStateSaver for testing
      import('./utils/GameStateSaver').then(({ GameStateSaver }) => {
        (window as any).GameStateSaver = GameStateSaver
      })

      // Expose SettingsManager for testing
      import('./game/SettingsManager').then(({ SettingsManager }) => {
        (window as any).SettingsManager = SettingsManager
      })

      // Expose GameModeFactory and GameMode for testing
      import('./game/core/GameModeFactory').then(({ GameModeFactory }) => {
        (window as any).GameModeFactory = GameModeFactory
      })
      import('./game/core/GameCore').then(({ GameMode }) => {
        (window as any).GameMode = GameMode
      })
    }
  } catch (error) {
    console.error('Failed to load game:', error)
    document.body.innerHTML = `
      <div style="
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100vh;
        background: #f9e7ef;
        color: #666;
        font-family: Arial, sans-serif;
        text-align: center;
      ">
        <div style="font-size: 48px; margin-bottom: 20px;">😞</div>
        <h2>Failed to load game</h2>
        <p>Please refresh the page to try again.</p>
        <button onclick="location.reload()" style="
          padding: 10px 20px;
          background: #8b5cf6;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
        ">Retry</button>
      </div>
    `
  }
}

window.addEventListener('load', initializeGame)
